import SimManager from "../managers/simManager";
import ControllableObject from "../interactableObjects/controllableObject";
import React from "react";
import { Vector3 } from "../types";
import CustomObject from "../interactableObjects/customObject";

const OVERLAP_THRESHOLD = 0.1;

enum mode {
  undefined = -1,
  idle = 0,
  dragging = 1,
}

// eslint-disable-next-line
const img = new Image();
img.src = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="; // A tiny transparent GIF

document.ondragover = (e) => e.preventDefault();

class controls {
  private object: object | undefined;
  private mode: mode;
  private startPos: Vector3;
  private initialMousePosition?: Vector3 | null;
  private scales: { userScale: number; sceneScale: number };

  constructor() {
    this.mode = mode.undefined;
    this.startPos = { x: 0, y: 0, z: 0 };
    this.scales = { userScale: 1, sceneScale: 1 };
  }

  removeControls(): boolean {
    if (this.mode == mode.undefined) return false;
    else {
      this.object = undefined;
      this.mode = mode.undefined;
      return true;
    }
  }

  // eslint-disable-next-line
  dragStart(e: React.DragEvent<HTMLDivElement>) {
    this.scales = SimManager.getInstance().getScales();
    const position = this.getTransform(e);
    this.startPos = {
      x: parseFloat(position.posX),
      y: parseFloat(position.posY),
      z: parseInt(e.currentTarget.style.zIndex),
    };
    this.initialMousePosition = null;
    e.dataTransfer.setDragImage(img, 0, 0);
    e.dataTransfer.effectAllowed = "move";
    this.mode = mode.dragging;
  }

  // eslint-disable-next-line
  dragging(
    e: React.DragEvent<HTMLDivElement>,
    item: ControllableObject | CustomObject
  ) {
    if (this.mode != mode.dragging) return;

    if (e.clientX <= 0 || e.clientY <= 0) return;

    if (!this.initialMousePosition) {
      this.initialMousePosition = {
        x: e.clientX / this.scales.sceneScale,
        y: e.clientY / this.scales.sceneScale,
        z: 0,
      };
    }

    e.dataTransfer.setDragImage(img, 0, 0);

    // Get the dragdrop-holder container bounds to constrain movement
    const dragdropContainer = document.getElementById("dragdrop-holder");
    let constrainedX =
      this.startPos.x +
      (e.clientX / this.scales.sceneScale - this.initialMousePosition.x);
    let constrainedY =
      this.startPos.y +
      (e.clientY / this.scales.sceneScale - this.initialMousePosition.y);

    // Apply boundary constraints if dragdrop container exists
    if (dragdropContainer) {
      const containerRect = dragdropContainer.getBoundingClientRect();
      const itemRect = e.currentTarget.getBoundingClientRect();

      // Get container dimensions in unscaled coordinates
      const containerWidth = containerRect.width / this.scales.sceneScale;
      const containerHeight = containerRect.height / this.scales.sceneScale;
      const itemWidth = itemRect.width / this.scales.sceneScale;
      const itemHeight = itemRect.height / this.scales.sceneScale;

      // Constrain within bounds
      constrainedX = Math.max(
        0,
        Math.min(constrainedX, containerWidth - itemWidth)
      );
      constrainedY = Math.max(
        0,
        Math.min(constrainedY, containerHeight - itemHeight)
      );
    }

    const newPos: Vector3 = {
      x: constrainedX,
      y: constrainedY,
      z: 0,
    };

    item.setItemPosition(newPos);
    e.currentTarget.style.zIndex = "10";
  }

  // eslint-disable-next-line
  dragEnd(
    e: React.DragEvent<HTMLDivElement>,
    callback: (_: HTMLDivElement | null) => void,
    itemID: string
  ) {
    // eslint-disable-next-line
    const draggedDiv = e.target as HTMLElement; // The div being dragged
    const draggedRect = draggedDiv.getBoundingClientRect(); // Get the bounds of the dragged div
    // eslint-disable-next-line

    let mostOverlapped: Element | null = null;
    let maxIOU = 0;
    e.dataTransfer.dropEffect = "none";

    // Convert boundingClientRect to bounding box format
    const bb1 = {
      x1: draggedRect.left,
      y1: draggedRect.top,
      x2: draggedRect.right,
      y2: draggedRect.bottom,
    };

    // eslint-disable-next-line
    document.querySelectorAll("div.graphics").forEach((element) => {
      if (element !== draggedDiv) {
        const targetRect = element.getBoundingClientRect(); // Get the bounds of the current element

        const bb2 = {
          x1: targetRect.left,
          y1: targetRect.top,
          x2: targetRect.right,
          y2: targetRect.bottom,
        };

        // Calculate IoU between dragged div and target div
        const iou = this.calculateIoU(bb1, bb2);

        // If IoU is above a certain threshold (e.g., 0.5 for 50% overlap)
        if (iou > OVERLAP_THRESHOLD && iou > maxIOU) {
          if (element.id != itemID) {
            maxIOU = iou;
            mostOverlapped = element;
          }
        }
      }
    });

    if (mostOverlapped) callback(mostOverlapped);
    else callback(null);

    this.mode = mode.idle;
    const position = this.getTransform(e);
    e.currentTarget.style.zIndex = this.startPos.z.toString();
    this.startPos = {
      x: parseFloat(position.posX),
      y: parseFloat(position.posY),
      z: this.startPos.z,
    };
  }

  drop() {
    //console.log("Dropped");
  }

  // eslint-disable-next-line
  getTransform(e: React.DragEvent<HTMLDivElement>) {
    const transform = e.currentTarget.style.transform;
    const regex = /-?\d*\.{0,1}\d+/g;
    const values = transform?.match(regex);
    let translateX = "";
    let translateY = "";

    if (values?.length) {
      if (values?.length == 2) {
        translateX = values[0];
        translateY = values[1];
      }
    }

    return {
      posX: translateX,
      posY: translateY,
    };
  }

  calculateIoU(
    bb1: { x1: number; x2: number; y1: number; y2: number },
    bb2: { x1: number; x2: number; y1: number; y2: number }
  ) {
    // Ensure the bounding boxes are valid
    if (
      !(bb1.x1 < bb1.x2) ||
      !(bb1.y1 < bb1.y2) ||
      !(bb2.x1 < bb2.x2) ||
      !(bb2.y1 < bb2.y2)
    ) {
      return 0.0;
    }

    // Determine the coordinates of the intersection rectangle
    const xLeft = Math.max(bb1.x1, bb2.x1);
    const yTop = Math.max(bb1.y1, bb2.y1);
    const xRight = Math.min(bb1.x2, bb2.x2);
    const yBottom = Math.min(bb1.y2, bb2.y2);

    // If there is no intersection
    if (xRight < xLeft || yBottom < yTop) {
      return 0.0;
    }

    // Calculate the area of the intersection rectangle
    const intersectionArea = (xRight - xLeft) * (yBottom - yTop);

    // Calculate the area of both bounding boxes
    const bb1Area = (bb1.x2 - bb1.x1) * (bb1.y2 - bb1.y1);

    // Calculate the Intersection over Union (IoU)
    const iou = intersectionArea / bb1Area;

    return Math.max(0.0, Math.min(iou, 1.0)); // IoU should be between 0 and 1
  }
}

export default controls;
